import { clipEndBar } from '@/audio/clipTiming';

export const BEAT_BARS = 0.25;

export const snapLoopBar = (bar) => Math.round(Number(bar || 0) / BEAT_BARS) * BEAT_BARS;

export const normalizeLoopRegion = ({ startBar = 0, endBar = 1 } = {}) => {
  const start = Math.max(0, snapLoopBar(Math.min(startBar, endBar)));
  const end = Math.max(start + BEAT_BARS, snapLoopBar(Math.max(startBar, endBar)));
  return { startBar: start, endBar: end };
};

export const loopRegionFromSelection = (tracks = [], selectedClipIds = new Set(), bpm = 90) => {
  let startBar = Infinity;
  let endBar = -Infinity;
  tracks.forEach((track) => (track.clips || []).forEach((clip) => {
    if (!selectedClipIds.has(clip.id)) return;
    startBar = Math.min(startBar, Number(clip.start_bar || 0));
    endBar = Math.max(endBar, clipEndBar(clip, bpm));
  }));
  return Number.isFinite(startBar) ? normalizeLoopRegion({ startBar, endBar }) : null;
};

export const loopRegionFromPlayhead = (playheadBar = 0) => {
  const startBar = Math.floor(Math.max(0, playheadBar));
  return { startBar, endBar: startBar + 4 };
};

/** Ruler drags snap to beats, preferring whole bars when close to one. */
export const loopRegionFromRuler = (startBar, endBar) => normalizeLoopRegion({ startBar, endBar });

/**
 * Loop pass start times in AudioContext seconds. The first pass contains fromBar
 * and all following entries start at the loop start.
 */
export const loopIterations = (region, bpm, fromBar = null) => {
  const normalized = normalizeLoopRegion(region);
  const secondsPerBar = 240 / bpm;
  const from = fromBar == null ? normalized.startBar : Math.max(normalized.startBar, fromBar);
  const firstStartBar = from;
  const durationBars = normalized.endBar - normalized.startBar;
  return {
    region: normalized,
    durationBars,
    durationSeconds: durationBars * secondsPerBar,
    firstStartBar,
    at: (iteration) => (iteration === 0 ? 0 : (normalized.endBar - firstStartBar + (iteration - 1) * durationBars) * secondsPerBar),
  };
};

export default loopIterations;

/**
 * One rule for every way of editing the loop bar on the ruler's loop strip.
 * Snaps to whole bars (beats with `fine`), never shorter than one snap step, never off the song.
 *   new   drag on empty strip: anchor -> current
 *   start drag the left handle    end  drag the right handle    move  drag the middle
 */
export const editLoopRegion = (region, mode, anchorBar, currentBar, { fine = false, totalBars = 32 } = {}) => {
  const step = fine ? BEAT_BARS : 1;
  const snap = (bar) => Math.round(Number(bar) / step) * step;
  const clamp = (bar, low, high) => Math.min(high, Math.max(low, bar));
  const start = Number(region?.startBar ?? 0);
  const end = Number(region?.endBar ?? step);
  if (mode === 'new') {
    let a = clamp(snap(anchorBar), 0, totalBars);
    let b = clamp(snap(currentBar), 0, totalBars);
    if (b < a) [a, b] = [b, a];
    if (b - a < step) b = Math.min(totalBars, a + step);
    if (b - a < step) a = Math.max(0, b - step);
    return { startBar: a, endBar: b };
  }
  if (mode === 'start') return { startBar: clamp(snap(currentBar), 0, end - step), endBar: end };
  if (mode === 'end') return { startBar: start, endBar: clamp(snap(currentBar), start + step, totalBars) };
  if (mode === 'move') {
    const length = end - start;
    const nextStart = clamp(start + snap(Number(currentBar) - Number(anchorBar)), 0, Math.max(0, totalBars - length));
    return { startBar: nextStart, endBar: nextStart + length };
  }
  return { startBar: start, endBar: end };
};

/** "Loop 3–6": 1-based bars, the end shown as the last bar inside the loop. */
export const loopLabel = ({ startBar, endBar }) => {
  const first = Math.floor(startBar) + 1;
  const last = Math.max(first, Math.ceil(endBar));
  return first === last ? `Loop bar ${first}` : `Loop ${first}–${last}`;
};
